import React, { useContext, useEffect } from 'react'
import { Field } from 'formik'
import { Input, NativeSelect } from '@dw/helix'

import features from '../../config/feature-flags'
import { ArticlesContext, AuthContext, UserGroup } from '../../contexts'
import { css } from '@emotion/core'
import { getSpecifiedReasonCodes, REASON_CODES } from '../ScanPage/reasonCodes'
import { BATTERY_CHANGE_ACCESS_GROUPS, INVENTORY_CONTROL_ACCESS_GROUPS } from '../ScanPage/ScanPage'
import CustomFormikInputField from './components/CustomFormikInputField'
import CustomFormikSearchField from './components/CustomFormikSearchField'
import CustomFormikNativeSelectField from './components/CustomFormikNativeSelectField'
import CustomFormikDatePickerField from './components/CustomFormikDatePickerField'
import { Article } from '../../types/DataTypes'

const spaceBetweenInputs = css`
  margin-bottom: 20px;
`

interface ScanFormProps {
  values: any
  errors: any
  initialValues: any
  selectedArticleId: string
  selectedOrderNumber: string
  onSetArticleId: (articleId: string) => void
  onSetFieldValue: (fieldValue: string, value: string | Date) => void
  onSetOrderNumber: (orderNumber: string) => void
}

const isDiscardOrRepairedEvent = (traceEvent: string) => ['Discarded', 'Repaired'].includes(traceEvent)

const ScanForm: React.FunctionComponent<ScanFormProps> = props => {
  const { hasGroup, userAttributes } = useContext(AuthContext)
  const { rawArticles } = useContext(ArticlesContext)
  const { locations = [] } = userAttributes
  const {
    values,
    errors,
    initialValues,
    onSetArticleId,
    selectedOrderNumber,
    selectedArticleId,
    onSetFieldValue,
    onSetOrderNumber,
  } = props

  useEffect(() => {
    onSetFieldValue('orderNumber', selectedOrderNumber)
    onSetFieldValue('articleId', selectedArticleId)
    onSetFieldValue('date', new Date)
  }, [onSetFieldValue, selectedOrderNumber, selectedArticleId])

  const traceTypeOptions = (() => {
    const options = [{ value: '', label: 'Select' }, { value: 'Inbound' }, { value: 'Outbound' }]

    if (hasGroup([UserGroup.Franchise])) return options

    if (features && features.traces_scanning_warrants) options.push({ value: 'Repaired' })

    if (hasGroup(BATTERY_CHANGE_ACCESS_GROUPS)) options.push({ value: 'Battery change' })

    if (hasGroup(INVENTORY_CONTROL_ACCESS_GROUPS)) options.push({ value: 'Inventory control' })

    return [...options, { value: 'Discarded' }, { value: 'Stolen' }, { value: 'Returned' }]
  })()

  const locationOptions = locations.map(value => ({ value }))

  const specifiedReasonCodes = getSpecifiedReasonCodes(values.reasonCode)

  return (
    <>
      <div css={spaceBetweenInputs}>
        <Field
          component={CustomFormikInputField}
          value={values.orderNumber}
          id="orderNumber"
          name="orderNumber"
          label="Order number"
          status={errors.orderNumber ? `error` : undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onSetOrderNumber(event.target.value.toUpperCase())
          }}
        />
      </div>
      <div css={spaceBetweenInputs}>
        <Field
          component={CustomFormikSearchField}
          value={values.articleId}
          onChangeValue={(value: string) => onSetArticleId(value)}
          suggestions={rawArticles
            .map(({ SKU }: Article) => SKU)
            .filter((sku: string) => sku.includes(values.articleId))}
          id="articleId"
          name="articleId"
          label="Article number"
          status={errors.articleId ? `error` : undefined}
        />
      </div>
      <div css={spaceBetweenInputs}>
        <Field
          component={CustomFormikNativeSelectField}
          value={values.traceEvent}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            onSetFieldValue('traceEvent', event.target.value)
            onSetFieldValue('reasonCode', initialValues.reasonCode)
            onSetFieldValue('specifiedReasonCode', initialValues.specifiedReasonCode)
          }}
          id="traceEvent"
          name="traceEvent"
          label="Select Trace Type"
          status={errors.traceEvent ? `error` : undefined}
          options={traceTypeOptions}
        />
      </div>

      {features &&
        features.traces_scanning_warrants &&
        !hasGroup([UserGroup.Franchise]) &&
        isDiscardOrRepairedEvent(values.traceEvent) && (
          <>
            <div css={spaceBetweenInputs}>
              <Field
                component={NativeSelect}
                value={values.reasonCode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onSetFieldValue('reasonCode', event.target.value)
                  onSetFieldValue('specifiedReasonCode', getSpecifiedReasonCodes(values.reasonCode)[0])
                }}
                id="reasonCode"
                name="reasonCode"
                label="Select Product Group"
                status={errors.reasonCode ? `error` : undefined}
                options={REASON_CODES.map(code => ({ value: code }))}
              />
            </div>
            <div css={spaceBetweenInputs}>
              <Field
                component={NativeSelect}
                value={values.specifiedReasonCode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onSetFieldValue('specifiedReasonCode', event.target.value)
                }
                id="specifiedReasonCode"
                name="specifiedReasonCode"
                label="Select Description"
                status={errors.specifiedReasonCode ? `error` : undefined}
                options={specifiedReasonCodes.map(code => ({
                  value: code,
                }))}
              />
            </div>
          </>
        )}

      <div css={spaceBetweenInputs}>
        <Field
          component={NativeSelect}
          value={values.location}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSetFieldValue('location', event.target.value)}
          id="location"
          name="location"
          label="Select Location"
          status={errors.location ? `error` : undefined}
          options={locationOptions}
        />
      </div>

      {hasGroup([UserGroup.Distributor]) && (
        <div css={spaceBetweenInputs}>
          <Field
            component={Input}
            value={values.shipTo}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSetFieldValue('shipTo', event.target.value)}
            id="shipTo"
            name="shipTo"
            label="Ship to"
            status={errors.shipTo ? `error` : undefined}
          />
        </div>
      )}
      <div css={spaceBetweenInputs}>
        <Field
          component={CustomFormikDatePickerField}
          id="date"
          name="date"
          status="normal"
          onChange={(value: Date) => onSetFieldValue('date', value)}
        />
      </div>
    </>
  )
}

export default ScanForm
